/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAppForm } from '@/components/form';
import { useCreateAndUpdateBewertungSchema } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/formSchema/useCreateAndUpdateBewertungSchema';
import z from 'zod';
import { useUpdateBewertung } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useUpdateBewertung/useUpdateBewertung';
import { Bewertung } from '@/domain/mitarbeiter/features/rotationsplan/entities/Bewertung';

export function useForm({
  bewertung,
  rotationsplanId,
  stationId,
}: {
  bewertung: Bewertung;
  rotationsplanId: string;
  stationId: string;
}) {
  const [updateBewertung] = useUpdateBewertung();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const schema = useCreateAndUpdateBewertungSchema();

  async function handleSubmit({
    sterne,
    beurteilung,
  }: {
    sterne: number;
    beurteilung: string;
  }) {
    try {
      const result = await updateBewertung({
        input: {
          id: bewertung.id,
          sterne,
          beurteilung,
          stationId,
        },
      });

      const success =
        result.data?.mitarbeiter_updateRotationsplanBewertung.success;

      if (success) {
        navigate(`/mitarbeiter/rotationsplan/${rotationsplanId}`);
        toast({
          title: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.updateBewertung.success',
          ),
        });
      } else {
        toast({
          title: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.updateBewertung.error',
          ),
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      toast({
        title: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.updateBewertung.error',
        ),
      });
    }
  }

  const form = useAppForm({
    defaultValues: (() => {
      return {
        sterne: bewertung.sterne,
        beurteilung: bewertung.beurteilung,
      } satisfies z.infer<typeof schema> as z.infer<typeof schema>;
    })(),
    validators: {
      onChange: schema,
      onBlur: schema,
      onSubmit: schema,
    },
    onSubmit: async ({ value }) =>
      await handleSubmit({
        beurteilung: value.beurteilung,
        sterne: value.sterne,
      }),
  });

  return form;
}
